import { memo, useState, useEffect, createElement as rc, useCallback } from 'react';
import { ProgressBar, Modal, fromTheme, Card, styled, webOnlyStyles, hooks } from 'lib_ui-primitives';
import useEventSink from '../../hooks/useEventSink';
import SyncProgressDetails from './SyncProgressDetails';
import logging from '@sstdev/lib_logging';
import lodash from 'lodash';
import { Body, CardHeader as _CardHeader, Content as _Content, HeaderText } from './styles';
const { isEqual, cloneDeep, set, omit } = lodash;

const { useTimeout } = hooks;

const DELAY_BEFORE_CLOSING = 100;

const MatchingCard = styled(Card)`
    width: 100%;
`;

const SkinnyModal = webOnlyStyles(Modal)`
    ${({ theme }) => !theme.mobile && 'height:unset;'}
`;

// todo: GL2025 remove this wrapper
const CardHeader = styled(_CardHeader)`
    padding: ${fromTheme('modal','progressDialog', 'padding')};
    color: ${fromTheme('modal','progressDialog', 'headerColor')};
`;

const Content = styled(_Content)`
    margin: ${fromTheme('progressDialog', 'margin')} 0;
`;

/**
 * Displays application progress events as numeric bars.
 * HTTP sync also supplies a collection list and cumulative per-collection updates
 * for the stable summary and optional details.
 */
const ProgressDialog = () => {
    const [open, setOpen] = useState(false);
    const [subscribe] = useEventSink();
    const [scrollingRequired, setScrollingRequired] = useState(false);
    /**
     * progress hash is constructed to look like this:
     * {
     *      mainTitle0: {
     *          description: <optional description string>,
     *          collections: <optional sync collection states keyed by namespace:relation>,
     *          title0: <some 0-100 int value>
     *          title1: <some 0-100 int value>
     *      }
     *      mainTitle1: {
     *          description: <optional description string>,
     *          title2: <some 0-100 int value>
     *          title3: <some 0-100 int value>
     *      }
     * }
     * each mainTitle results in a separate card in the dialog
     * each description describes the data in the card
     * each title results in a numeric progress bar inside the card
     */
    const [progress, setProgress] = useState({});

    const closeTimeout = useTimeout(() => setOpen(false), [], DELAY_BEFORE_CLOSING);
    const updateProgress = useCallback(
        progressChange => {
            if (progressChange == null) {
                logging.error('Application progress events must include a payload.');
                return;
            }

            const { mainTitle, title, description, current, total, collections, collection } = progressChange;
            if (mainTitle == null) {
                logging.error('Application progress events must include a main title in the payload.');
                return;
            }
            setProgress(prev => {
                // deep clone to avoid previous value having the same internal ref and
                // getting updated by accident
                let newProgress = cloneDeep(prev);
                // A payload without numeric progress or collection updates resets the entry.
                // If a `title` is provided, just remove that one single bar.
                // If that was the last progress bar for that `mainTitle`, or if no `title` is provided,
                // remove the entire progress bar container.
                if (total == null && collections == null && collection == null) {
                    if (title == null) {
                        delete newProgress[mainTitle];
                    } else if (newProgress?.[mainTitle]?.[title]) {
                        delete newProgress[mainTitle][title];
                        //if at most `description` is left:
                        if (Object.keys(omit(newProgress[mainTitle], 'description')).length <= 0) {
                            //remove this whole progress object
                            delete newProgress[mainTitle];
                        }
                    }
                } else {
                    // Don't remove description if a subsequent progress updated does
                    // not have it.
                    if (description != null) {
                        set(newProgress, [mainTitle, 'description'], description);
                    }
                    // Replayed collection lists fill in missed startup events without resetting batch counts.
                    if (collections != null) {
                        const previous = newProgress[mainTitle]?.collections;
                        set(
                            newProgress,
                            [mainTitle, 'collections'],
                            Object.fromEntries(collections.map(row => [row.key, { ...row, ...previous?.[row.key] }]))
                        );
                    }
                    if (collection != null) {
                        const previous = newProgress[mainTitle]?.collections?.[collection.key];
                        set(newProgress, [mainTitle, 'collections', collection.key], {
                            recordsReceived: 0,
                            deletionsProcessed: 0,
                            ...previous,
                            ...collection
                        });
                    }
                    if (total != null) {
                        const percentage = Math.ceil((current / total) * 100);
                        set(newProgress, [mainTitle, title], current < total ? Math.min(99, percentage) : percentage);
                    }
                }
                // avoid unnecessary renders by retaining the previous ref if nothing changed.
                return isEqual(prev, newProgress) ? prev : newProgress;
            });
        },
        [setProgress]
    );

    useEffect(() => {
        const numberCards = Object.entries(progress).length;

        setScrollingRequired(numberCards > 1);
        // if there are zero mainTitle entries:
        // Wait DELAY_BEFORE_CLOSING mills for user to see the previous update
        // and then close the dialog
        if (numberCards === 0) {
            closeTimeout();
        } else {
            // Open the dialog if there are any mainTitle entries
            setOpen(true);
        }
    }, [progress, closeTimeout]);

    useEffect(() => {
        const unsubscribes = [
            subscribe({ verb: 'update', namespace: 'application', relation: 'progress' }, updateProgress),
            subscribe({ verb: 'reset', namespace: 'application', relation: 'progress' }, updateProgress)
        ];
        return () => {
            unsubscribes.forEach(u => u());
        };
    }, [subscribe, updateProgress]);

    // prettier-ignore
    return rc(SkinnyModal, { id: 'progressDialogModal', visible: open, scrollingRequired, 'data-testid': 'modal-content' },
        Object.entries(progress).map(([key, value]) => {
            const { description, collections, ...scrollbars } = value;
            return rc(MatchingCard, { key },
                rc(CardHeader, null,
                    key != null && rc(HeaderText, null, key)
                ),
                rc(Body, null,
                    description != null && rc(Content, null, description),
                    Object.entries(scrollbars).map(([key, value]) => {
                        return rc(ProgressBar, { key, progress: value, title: key });
                    }),
                    collections != null && rc(SyncProgressDetails, { collections })
                )
            );
        })
    );
};

export default memo(ProgressDialog);
